/**
 * Autosize a column to its content — the double-click on the resize gutter (P-107, `[B4§8.2]`).
 *
 * §8.2 records the behaviour as *"double-click the edge = autosize to content"*, **ASSUMED**
 * (Q165), and the first implementation took the cheapest reading of it: it sent the registry
 * default for the column *type*, so a `status` column snapped to 140 px whatever was in it and
 * a column of long text snapped to the same number as an empty one — which cannot reveal a
 * truncated value, the only reason to use the gesture.
 *
 * What it measures is the **mounted** rows. The grid is virtualised, so those are the only
 * cells that exist; monday cannot measure an unloaded row either, and Q165 stays open on
 * whether it tries. The header's own title is included, because a column narrower than its
 * title is not sized to its content.
 *
 * The arithmetic is here and the DOM reading is the one function below it, so the rule is
 * testable without a browser.
 */
import { clampWidth, COLUMN_WIDTH_MIN } from './layout';

/** `.ez-cell` is `padding: 0 8px` (`table.css`). */
export const CELL_PADDING = 16;
/**
 * What a header needs beside its title: the 16 px type icon, its 6 px gap, the `⌄` menu button
 * and the 8 px gutter either side (`table.css` `.ez-column-header`).
 */
export const HEADER_CHROME = 58;

export interface AutosizeInput {
  /** The rendered text of every mounted cell of the column, in any order. */
  readonly cellTexts: readonly string[];
  readonly headerTitle: string;
  /** Text width in px, at the font the grid paints with. */
  readonly measure: (text: string) => number;
  /** The column's resize floor — higher than `COLUMN_WIDTH_MIN` for the name column. */
  readonly min?: number;
  /** The column type's registry default: what an **empty** column autosizes to. */
  readonly fallback: number;
}

/**
 * The width a double-click commits: the widest mounted cell (plus the cell's padding) or the
 * header's own title (plus the header's chrome), whichever is wider, clamped to the column's
 * min/max. A column with nothing in it keeps the type's default, which is the only number
 * there is to fall back on.
 */
export function autosizeWidth({
  cellTexts,
  headerTitle,
  measure,
  min = COLUMN_WIDTH_MIN,
  fallback,
}: AutosizeInput): number {
  let widest = 0;
  for (const text of cellTexts) {
    const trimmed = text.trim();
    if (trimmed === '') continue;
    widest = Math.max(widest, measure(trimmed));
  }
  if (widest === 0) return clampWidth(fallback, min);
  const header = headerTitle.trim() === '' ? 0 : measure(headerTitle.trim()) + HEADER_CHROME;
  return clampWidth(Math.max(widest + CELL_PADDING, header), min);
}

/** One canvas for the whole session: `measureText` is cheap, creating a context is not. */
let context: CanvasRenderingContext2D | null = null;
function measurer(font: string): (text: string) => number {
  if (context === null) context = document.createElement('canvas').getContext('2d');
  const ctx = context;
  if (!ctx) {
    // No canvas (jsdom without one): 7.2 px per character is the 14 px grid font's average.
    return (text) => text.length * 7.2;
  }
  ctx.font = font;
  return (text) => ctx.measureText(text).width;
}

/**
 * The DOM half: read the mounted cells of one column and answer the width to commit.
 *
 * `.ez-cell[data-column]` is every cell of the column in both halves of the row — the sticky
 * one and the scrolling one — and `textContent` is what the reader sees, whatever the type's
 * renderer paints it with.
 */
export function measureColumnWidth(input: {
  columnId: string;
  headerTitle: string;
  fallback: number;
  min?: number;
  root?: ParentNode;
}): number {
  const root = input.root ?? document;
  const selector = `.ez-cell[data-column="${CSS.escape(input.columnId)}"]`;
  const cells = [...root.querySelectorAll<HTMLElement>(selector)];
  const font =
    (cells[0] ? getComputedStyle(cells[0]).font : '') || '400 14px Figtree, Roboto, sans-serif';
  return autosizeWidth({
    cellTexts: cells.map((cell) => cell.textContent ?? ''),
    headerTitle: input.headerTitle,
    measure: measurer(font),
    ...(input.min === undefined ? {} : { min: input.min }),
    fallback: input.fallback,
  });
}
